import type { RaRecord } from "react-admin";
import dayjs from "dayjs";

import {
  findSavedHallConfigEntry,
  type TallyConfig,
  type TallyHallConfig,
  type TallyPaymentLedgers,
  type TallySabilTypeKey,
} from "@/types/tally-config";

import type { TallyVoucherRow } from "./export-to-tally-excel";
import { formatLagatReceiptNarrationFromRecord } from "./lagat-receipt-narration";
import { formatNiyaazReceiptNarrationFromRecord } from "./niyaaz-receipt-narration";
import { formatRentBookingReceiptNarrationFromRecord } from "./rent-booking-receipt-narration";
import { formatSabilReceiptNarrationFromRecord } from "./sabil-receipt-narration";
import { formatYearlyNiyaazReceiptNarrationFromRecord } from "./yearly-niyaaz-receipt-narration";
import { formatFmbReceiptNarrationFromRecord } from "./fmb-receipt-narration";
import type { TallyNarrationOptions } from "./tally-narration-options";

export type TallyExportModule = "lagat" | "sabil" | "rent" | "niyaaz" | "yearlyNiyaaz" | "fmb";

export type TallyReceiptOverride = {
  /** Rent: map booking hall name → configured hall key in Tally config. */
  rentHallMap?: Record<string, string>;
};

export type TallySkippedReceipt = {
  receiptId: string;
  receiptNo: string;
  reasons: string[];
};

export type TallyPreviewRow = TallyVoucherRow & {
  id: string;
  rowIssues: string[];
};

export type TallyExportPreview = {
  rows: TallyPreviewRow[];
  skipped: TallySkippedReceipt[];
};

export type TallyManualVoucherForm = {
  receiptNo: string;
  date: string;
  paymentMode: string;
  amount: number;
  incomeLedger: string;
  drLedger: string;
  costCategory: string;
  costCentre: string;
  ledgerNarration?: string;
};

const VOUCHER_TYPE = "Receipt";

function tallyTrim(value: unknown): string {
  return String(value ?? "").trim();
}

function receiptId(rec: RaRecord): string {
  return String(rec.id ?? rec.receiptNo ?? "");
}

function receiptLabel(rec: RaRecord): string {
  return tallyTrim(rec.receiptNo) || receiptId(rec) || "—";
}

function parseAmount(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

function formatTallyDate(v: unknown): Date | null {
  if (v == null || v === "") return null;
  const d = dayjs(v as string | Date);
  return d.isValid() ? d.toDate() : null;
}

function drLedgerForMode(ledgers: TallyPaymentLedgers, paymentMode: unknown): string {
  const mode = tallyTrim(paymentMode).toUpperCase();
  const ledger = mode === "CASH" ? ledgers.cashLedger : ledgers.bankLedger;
  return tallyTrim(ledger);
}

function costAllocationFields(
  categoryName: unknown,
  costCentre: unknown,
  amount: number
): Pick<TallyVoucherRow, "categoryName" | "costCentre" | "costAmount"> {
  const category = tallyTrim(categoryName);
  const centre = tallyTrim(costCentre);
  if (!category || !centre || amount <= 0) {
    return {};
  }
  return { categoryName: category, costCentre: centre, costAmount: amount };
}

function splitAmount(total: number, parts: number): number[] {
  if (parts <= 0) return [];
  const base = Math.floor(total / parts);
  const amounts = Array.from({ length: parts }, () => base);
  const remainder = total - base * parts;
  if (amounts.length > 0) {
    amounts[amounts.length - 1] += remainder;
  }
  return amounts;
}

function buildReceiptVoucher(opts: {
  date: unknown;
  receiptNo: unknown;
  paymentMode: unknown;
  amount: number;
  voucherNarration: string;
  paymentLedgers: TallyPaymentLedgers;
  crLines: Array<{
    ledgerName: string;
    amount: number;
    ledgerNarration?: string;
    categoryName?: string;
    costCentre?: string;
  }>;
}): { rows: TallyVoucherRow[]; reasons: string[] } {
  const reasons: string[] = [];
  const crLines = opts.crLines.filter((cr) => cr.amount > 0 && tallyTrim(cr.ledgerName));
  if (crLines.length === 0) {
    reasons.push("No credit (income) lines — check income ledger and amount.");
  }

  const crTotal = crLines.reduce((sum, cr) => sum + cr.amount, 0);
  const drAmount = crTotal > 0 ? crTotal : opts.amount;
  if (drAmount <= 0) {
    reasons.push("Amount must be greater than zero.");
  }

  const drLedger = drLedgerForMode(opts.paymentLedgers, opts.paymentMode);
  if (!drLedger) {
    reasons.push("Debit ledger missing for payment mode (configure cash/bank in Tally config).");
  }

  if (reasons.length > 0) {
    return { rows: [], reasons };
  }

  const voucherNumber = tallyTrim(opts.receiptNo) || "—";
  const voucherDate = formatTallyDate(opts.date);
  const voucherNarration = opts.voucherNarration;

  const result: TallyVoucherRow[] = [];
  let first = true;
  for (const cr of crLines) {
    result.push({
      voucherDate: first ? voucherDate : null,
      voucherType: first ? VOUCHER_TYPE : "",
      voucherNumber: first ? voucherNumber : "",
      ledgerName: tallyTrim(cr.ledgerName),
      amount: cr.amount,
      drCr: "Cr",
      voucherNarration: first ? voucherNarration : null,
      ledgerNarration: tallyTrim(cr.ledgerNarration) || null,
      ...costAllocationFields(cr.categoryName, cr.costCentre, cr.amount),
    });
    first = false;
  }

  result.push({
    voucherDate: null,
    voucherType: "",
    voucherNumber: "",
    ledgerName: drLedger,
    amount: drAmount,
    drCr: "Dr",
  });

  return { rows: result, reasons: [] };
}

function resolveSabilType(raw: unknown): TallySabilTypeKey {
  const t = tallyTrim(raw).toUpperCase();
  if (t === "ESTABLISHMENT" || t === "PROFESSIONAL") return "ESTABLISHMENT";
  if (t === "MUTTAVATTEEN_YEARLY") return "MUTTAVATTEEN_YEARLY";
  if (t === "MUTTAVATTEEN") return "MUTTAVATTEEN";
  return "CHULA";
}

function hallNamesFromBooking(rec: RaRecord): string[] {
  const hallsRaw = (
    rec.booking as { hallBookings?: Array<{ hall?: { name?: string } }> } | undefined
  )?.hallBookings;
  return [
    ...new Set(
      (hallsRaw ?? []).map((hb) => hb.hall?.name?.trim()).filter((n): n is string => Boolean(n))
    ),
  ];
}

type ReceiptBuildOutcome =
  | { status: "ok"; rows: TallyVoucherRow[] }
  | { status: "skipped"; reasons: string[] };

function buildLagatShapedReceipt(
  rec: RaRecord,
  block: TallyConfig["lagat"],
  opts: {
    date: unknown;
    paymentMode: unknown;
    narration: string;
  }
): ReceiptBuildOutcome {
  const amount = parseAmount(rec.amount);
  if (amount <= 0) {
    return { status: "skipped", reasons: ["Amount is zero or invalid."] };
  }
  const { rows, reasons } = buildReceiptVoucher({
    date: opts.date,
    receiptNo: rec.receiptNo,
    paymentMode: opts.paymentMode,
    amount,
    paymentLedgers: block,
    voucherNarration: opts.narration,
    crLines: [
      {
        ledgerName: tallyTrim(block.incomeLedger),
        amount,
        ledgerNarration: opts.narration,
        categoryName: block.costCategory,
        costCentre: block.costCentre,
      },
    ],
  });
  if (rows.length === 0) return { status: "skipped", reasons };
  return { status: "ok", rows };
}

function buildLagatReceipt(
  rec: RaRecord,
  config: TallyConfig,
  _override?: TallyReceiptOverride,
  narrationOptions?: TallyNarrationOptions
): ReceiptBuildOutcome {
  return buildLagatShapedReceipt(rec, config.lagat, {
    date: rec.receiptDate,
    paymentMode: rec.paymentMode,
    narration: formatLagatReceiptNarrationFromRecord(rec, narrationOptions),
  });
}

function buildNiyaazReceipt(
  rec: RaRecord,
  config: TallyConfig,
  _override?: TallyReceiptOverride,
  narrationOptions?: TallyNarrationOptions
): ReceiptBuildOutcome {
  return buildLagatShapedReceipt(rec, config.niyaaz, {
    date: rec.date,
    paymentMode: rec.mode,
    narration: formatNiyaazReceiptNarrationFromRecord(rec, narrationOptions),
  });
}

function buildYearlyNiyaazReceipt(
  rec: RaRecord,
  config: TallyConfig,
  _override?: TallyReceiptOverride,
  narrationOptions?: TallyNarrationOptions
): ReceiptBuildOutcome {
  return buildLagatShapedReceipt(rec, config.yearlyNiyaaz, {
    date: rec.receiptDate,
    paymentMode: rec.paymentMode,
    narration: formatYearlyNiyaazReceiptNarrationFromRecord(rec, narrationOptions),
  });
}

function buildSabilReceipt(
  rec: RaRecord,
  config: TallyConfig,
  _override?: TallyReceiptOverride,
  narrationOptions?: TallyNarrationOptions
): ReceiptBuildOutcome {
  const amount = parseAmount(rec.amount);
  if (amount <= 0) {
    return { status: "skipped", reasons: ["Amount is zero or invalid."] };
  }
  const sabilData = rec.sabilData as Record<string, unknown> | undefined;
  const typeKey = resolveSabilType(sabilData?.sabilType);
  const typeCfg = config.sabil[typeKey];
  const narration = formatSabilReceiptNarrationFromRecord(rec, narrationOptions);
  const { rows, reasons } = buildReceiptVoucher({
    date: rec.receiptDate,
    receiptNo: rec.receiptNo,
    paymentMode: rec.paymentMode,
    amount,
    paymentLedgers: config.sabil,
    voucherNarration: narration,
    crLines: [
      {
        ledgerName: tallyTrim(typeCfg.incomeLedger),
        amount,
        ledgerNarration: narration,
        categoryName: config.sabil.costCategory,
        costCentre: typeCfg.costCentre,
      },
    ],
  });
  if (rows.length === 0) return { status: "skipped", reasons };
  return { status: "ok", rows };
}

function buildRentReceipt(
  rec: RaRecord,
  config: TallyConfig,
  override?: TallyReceiptOverride,
  narrationOptions?: TallyNarrationOptions
): ReceiptBuildOutcome {
  if (tallyTrim(rec.type).toUpperCase() === "DEPOSIT") {
    return { status: "skipped", reasons: ["Deposit receipts are not exported to Tally."] };
  }
  const amount = parseAmount(rec.amount);
  if (amount <= 0) {
    return { status: "skipped", reasons: ["Amount is zero or invalid."] };
  }

  const hallNames = hallNamesFromBooking(rec);
  if (hallNames.length === 0) {
    return { status: "skipped", reasons: ["Booking has no halls linked."] };
  }

  const missingHalls: string[] = [];
  const parts: Array<{ name: string; hall: TallyHallConfig }> = [];

  for (const name of hallNames) {
    const mapped = override?.rentHallMap?.[name] ?? name;
    const entry = findSavedHallConfigEntry(config.rent.halls, mapped);
    if (!entry) {
      missingHalls.push(name);
    } else {
      parts.push({ name, hall: entry.config });
    }
  }

  if (missingHalls.length > 0) {
    return {
      status: "skipped",
      reasons: [
        `Hall(s) not in Tally config: ${missingHalls.join(", ")}. Map each hall below or update Accounts → Tally config.`,
      ],
    };
  }

  const narration = formatRentBookingReceiptNarrationFromRecord(rec, narrationOptions);
  const split = splitAmount(amount, parts.length);
  const crLines = parts.map((p, i) => ({
    ledgerName: tallyTrim(p.hall.incomeLedger),
    amount: split[i] ?? 0,
    ledgerNarration: narration,
    categoryName: p.hall.costCategory,
    costCentre: p.hall.costCentre,
  }));

  const { rows, reasons } = buildReceiptVoucher({
    date: rec.date,
    receiptNo: rec.receiptNo,
    paymentMode: rec.mode,
    amount,
    paymentLedgers: parts[0].hall,
    voucherNarration: narration,
    crLines,
  });
  if (rows.length === 0) return { status: "skipped", reasons };
  return { status: "ok", rows };
}

function isFmbStreamMapped(stream: {
  incomeLedger: string;
  costCategory: string;
  costCentre: string;
}): boolean {
  return Boolean(
    tallyTrim(stream.incomeLedger) && tallyTrim(stream.costCategory) && tallyTrim(stream.costCentre)
  );
}

function buildFmbReceipt(
  rec: RaRecord,
  config: TallyConfig,
  _override?: TallyReceiptOverride,
  narrationOptions?: TallyNarrationOptions
): ReceiptBuildOutcome {
  const amount = parseAmount(rec.amount);
  if (amount <= 0) {
    return { status: "skipped", reasons: ["Amount is zero or invalid."] };
  }

  const fmb = config.fmb;
  const lines = Array.isArray(rec.allocations) ? rec.allocations : [];
  let annualAmount = 0;
  let contributionAmount = 0;

  for (const line of lines) {
    const row = line as {
      amount?: unknown;
      fmbTakhmeenId?: unknown;
      fmbContributionId?: unknown;
    };
    const lineAmount = parseAmount(row.amount);
    if (lineAmount <= 0) continue;
    if (tallyTrim(row.fmbContributionId)) {
      contributionAmount += lineAmount;
    } else if (tallyTrim(row.fmbTakhmeenId)) {
      annualAmount += lineAmount;
    }
  }

  annualAmount += parseAmount(rec.unallocatedAmount);

  // Credit-only / no allocation lines: put the full receipt on Annual.
  if (annualAmount <= 0 && contributionAmount <= 0) {
    annualAmount = amount;
  }

  const reasons: string[] = [];
  if (annualAmount > 0 && !isFmbStreamMapped(fmb.annual)) {
    reasons.push("FMB Annual income/cost ledgers are not configured.");
  }
  if (contributionAmount > 0 && !isFmbStreamMapped(fmb.contribution)) {
    reasons.push("FMB Contribution income/cost ledgers are not configured.");
  }
  if (reasons.length > 0) {
    return { status: "skipped", reasons };
  }

  const narration = formatFmbReceiptNarrationFromRecord(rec, narrationOptions);
  const crLines: Array<{
    ledgerName: string;
    amount: number;
    ledgerNarration?: string;
    categoryName?: string;
    costCentre?: string;
  }> = [];

  if (annualAmount > 0) {
    crLines.push({
      ledgerName: tallyTrim(fmb.annual.incomeLedger),
      amount: annualAmount,
      ledgerNarration: narration,
      categoryName: fmb.annual.costCategory,
      costCentre: fmb.annual.costCentre,
    });
  }
  if (contributionAmount > 0) {
    crLines.push({
      ledgerName: tallyTrim(fmb.contribution.incomeLedger),
      amount: contributionAmount,
      ledgerNarration: narration,
      categoryName: fmb.contribution.costCategory,
      costCentre: fmb.contribution.costCentre,
    });
  }

  const { rows, reasons: voucherReasons } = buildReceiptVoucher({
    date: rec.receiptDate,
    receiptNo: rec.receiptNo,
    paymentMode: rec.paymentMode,
    amount,
    paymentLedgers: fmb,
    voucherNarration: narration,
    crLines,
  });
  if (rows.length === 0) return { status: "skipped", reasons: voucherReasons };
  return { status: "ok", rows };
}

function buildReceiptForModule(
  module: TallyExportModule,
  rec: RaRecord,
  config: TallyConfig,
  override?: TallyReceiptOverride,
  narrationOptions?: TallyNarrationOptions
): ReceiptBuildOutcome {
  switch (module) {
    case "lagat":
      return buildLagatReceipt(rec, config, override, narrationOptions);
    case "sabil":
      return buildSabilReceipt(rec, config, override, narrationOptions);
    case "rent":
      return buildRentReceipt(rec, config, override, narrationOptions);
    case "niyaaz":
      return buildNiyaazReceipt(rec, config, override, narrationOptions);
    case "yearlyNiyaaz":
      return buildYearlyNiyaazReceipt(rec, config, override, narrationOptions);
    case "fmb":
      return buildFmbReceipt(rec, config, override, narrationOptions);
    default:
      return { status: "skipped", reasons: ["Unknown export module."] };
  }
}

let previewRowCounter = 0;

function toPreviewRows(rows: TallyVoucherRow[], receiptIdPrefix: string): TallyPreviewRow[] {
  return rows.map((row, index) => {
    previewRowCounter += 1;
    return {
      ...row,
      id: `${receiptIdPrefix}-${index}-${previewRowCounter}`,
      rowIssues: [],
    };
  });
}

export function buildManualVoucherRows(form: TallyManualVoucherForm): TallyVoucherRow[] {
  const amount = Math.round(Number(form.amount));
  if (amount <= 0) return [];

  const dr = tallyTrim(form.drLedger);
  const isCash = tallyTrim(form.paymentMode).toUpperCase() === "CASH";
  const paymentLedgers: TallyPaymentLedgers = {
    cashLedger: isCash ? dr : "",
    bankLedger: isCash ? "" : dr,
  };

  const { rows } = buildReceiptVoucher({
    date: form.date,
    receiptNo: form.receiptNo,
    paymentMode: form.paymentMode,
    amount,
    paymentLedgers,
    voucherNarration: "",
    crLines: [
      {
        ledgerName: form.incomeLedger,
        amount,
        ledgerNarration: form.ledgerNarration,
        categoryName: form.costCategory,
        costCentre: form.costCentre,
      },
    ],
  });
  return rows;
}

export function auditPreviewRows(rows: TallyPreviewRow[]): TallyPreviewRow[] {
  const fieldIssues = rows.map((row) => {
    const issues: string[] = [];
    if (!tallyTrim(row.ledgerName)) issues.push("Ledger name is empty.");
    if (row.amount <= 0) issues.push("Amount must be greater than zero.");
    if (row.drCr !== "Dr" && row.drCr !== "Cr") issues.push('Dr/Cr must be "Dr" or "Cr".');
    if (row.drCr === "Cr" && tallyTrim(row.categoryName) && !tallyTrim(row.costCentre)) {
      issues.push("Cost centre required when category is set.");
    }
    if (row.drCr === "Cr" && tallyTrim(row.costCentre) && !tallyTrim(row.categoryName)) {
      issues.push("Cost category required when cost centre is set.");
    }
    return { ...row, rowIssues: [...row.rowIssues, ...issues] };
  });

  const groups: TallyPreviewRow[][] = [];
  let current: TallyPreviewRow[] = [];
  for (const row of fieldIssues) {
    if (tallyTrim(row.voucherNumber)) {
      if (current.length) groups.push(current);
      current = [row];
    } else {
      current.push(row);
    }
  }
  if (current.length) groups.push(current);

  const imbalanceById = new Map<string, string[]>();
  for (const group of groups) {
    const cr = group.filter((r) => r.drCr === "Cr").reduce((s, r) => s + r.amount, 0);
    const dr = group.filter((r) => r.drCr === "Dr").reduce((s, r) => s + r.amount, 0);
    if (cr !== dr) {
      const label = tallyTrim(group[0]?.voucherNumber) || "voucher";
      const msg = `${label}: total Cr (${cr}) ≠ total Dr (${dr}).`;
      for (const r of group) {
        imbalanceById.set(r.id, [...(imbalanceById.get(r.id) ?? []), msg]);
      }
    }
  }

  return fieldIssues.map((row) => ({
    ...row,
    rowIssues: [...row.rowIssues, ...(imbalanceById.get(row.id) ?? [])],
  }));
}

export function hasBlockingPreviewIssues(rows: TallyPreviewRow[]): boolean {
  return rows.some((r) => r.rowIssues.length > 0);
}

export function voucherKeyForGroup(group: TallyPreviewRow[]): string {
  return tallyTrim(group[0]?.voucherNumber) || group[0]?.id || "";
}

export type TallyProblemVoucher = {
  key: string;
  voucherNumber: string;
  lineCount: number;
  issues: string[];
};

export function listProblemVouchers(rows: TallyPreviewRow[]): TallyProblemVoucher[] {
  return groupVoucherRows(rows)
    .filter((group) => group.some((r) => r.rowIssues.length > 0))
    .map((group) => ({
      key: voucherKeyForGroup(group),
      voucherNumber: tallyTrim(group[0].voucherNumber) || "—",
      lineCount: group.length,
      issues: [...new Set(group.flatMap((r) => r.rowIssues))],
    }));
}

/** Omit voucher groups the user chose to ignore (by voucher / receipt no. key). */
export function filterExportRowsByIgnoredVouchers(
  rows: TallyPreviewRow[],
  ignoredVoucherKeys: ReadonlySet<string>
): TallyPreviewRow[] {
  let currentKey = "";
  return rows.filter((row) => {
    if (tallyTrim(row.voucherNumber)) {
      currentKey = tallyTrim(row.voucherNumber);
    }
    const key = currentKey || row.id;
    return !ignoredVoucherKeys.has(key);
  });
}

export function groupVoucherRows(rows: TallyPreviewRow[]): TallyPreviewRow[][] {
  const groups: TallyPreviewRow[][] = [];
  let current: TallyPreviewRow[] = [];
  for (const row of rows) {
    if (tallyTrim(row.voucherNumber)) {
      if (current.length) groups.push(current);
      current = [row];
    } else {
      current.push(row);
    }
  }
  if (current.length) groups.push(current);
  return groups;
}

/** Drop whole vouchers if any line in the group has validation issues. */
export function excludeVouchersWithIssues(rows: TallyPreviewRow[]): TallyPreviewRow[] {
  return groupVoucherRows(rows)
    .filter((group) => group.every((r) => r.rowIssues.length === 0))
    .flat();
}

export function countVouchersWithIssues(rows: TallyPreviewRow[]): number {
  return groupVoucherRows(rows).filter((group) => group.some((r) => r.rowIssues.length > 0)).length;
}

export function buildTallyExportPreview(
  module: TallyExportModule,
  records: RaRecord[],
  config: TallyConfig,
  overrides: Record<string, TallyReceiptOverride> = {},
  narrationOptions?: TallyNarrationOptions
): TallyExportPreview {
  previewRowCounter = 0;
  const rows: TallyPreviewRow[] = [];
  const skipped: TallySkippedReceipt[] = [];

  for (const rec of records) {
    const id = receiptId(rec);
    const outcome = buildReceiptForModule(module, rec, config, overrides[id], narrationOptions);
    if (outcome.status === "ok") {
      rows.push(...toPreviewRows(outcome.rows, id || receiptLabel(rec)));
    } else {
      skipped.push({
        receiptId: id,
        receiptNo: receiptLabel(rec),
        reasons: outcome.reasons,
      });
    }
  }

  return { rows: auditPreviewRows(rows), skipped };
}

export function previewRowsToVoucherRows(rows: TallyPreviewRow[]): TallyVoucherRow[] {
  return rows.map(({ id: _id, rowIssues: _issues, ...row }) => row);
}

export function rentHallMappingOptions(config: TallyConfig): string[] {
  return Object.keys(config.rent.halls).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );
}

export function extractRentMissingHalls(reasons: string[]): string[] {
  const line = reasons.find((r) => r.startsWith("Hall(s) not in Tally config:"));
  if (!line) return [];
  const list = line.replace("Hall(s) not in Tally config:", "").trim();
  return list
    .replace(/\.$/, "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}
